import { useSyncExternalStoreWithSelector } from "use-sync-external-store/shim/with-selector";
import { deepEqual } from "fast-equals";
import { cartStore, type CartStoreState } from "src/stores/cart/cart.store";
import type { CartInfo } from "src/types/cartInfo";
import type { Money } from "src/types/ctgraphql.d";

export function useCartStore<T>(
  selector: (state: CartStoreState) => T,
  isEqual: (a: T, b: T) => boolean = Object.is
): T {
  return useSyncExternalStoreWithSelector(
    cartStore.subscribe,
    cartStore.getSnapshot,
    cartStore.getServerSnapshot,
    selector,
    isEqual
  );
}

const selectCart = (state: CartStoreState) => state.cart;
const selectTotalItems = (state: CartStoreState) => state.totalItems;
const selectLineItemsTotal = (state: CartStoreState) => state.lineItemsTotal;
const selectHasFreeShipping = (state: CartStoreState) => state.hasFreeShipping;
const selectLoading = (state: CartStoreState) => state.loading;
const selectHasLoaded = (state: CartStoreState) => state.hasLoaded;
const selectCartInfo = (state: CartStoreState) => ({
  id: state.id,
  version: state.version,
});

export function useCartData() {
  const cart = useCartStore(selectCart);
  const loading = useCartStore(selectLoading);
  const hasLoaded = useCartStore(selectHasLoaded);

  return { cart, loading, hasLoaded };
}

export function useCartTotalItems(): number {
  return useCartStore(selectTotalItems);
}

export function useCartLineItemsTotal(): Money | null {
  return useCartStore(selectLineItemsTotal);
}

export function useCartHasFreeShipping(): boolean {
  return useCartStore(selectHasFreeShipping);
}

export function useCartInfo(): CartInfo {
  return useCartStore(selectCartInfo, deepEqual);
}
